import { Editor } from '@tiptap/react';

export function checkSelectionHasRestrictedNodes(editor: Editor) {
    const { state } = editor;
    const { from, to } = state.selection;
    const { doc, schema } = state;

    let hasTable = false;
    let hasImage = false;
    let hasIframe = false;

    const tableNode = schema.nodes?.table;
    const imageNode = schema.nodes?.image;
    const iframeNode = schema.nodes?.iframe || schema.nodes?.video || schema.nodes?.embed;

    doc.nodesBetween(from, to, (node) => {
        if (tableNode && node.type === tableNode) hasTable = true;
        if (imageNode && node.type === imageNode) hasImage = true;
        if (iframeNode && node.type === iframeNode) hasIframe = true;
    });

    return hasTable || hasImage || hasIframe;
}

export function checkMixedSelection(editor: Editor) {
    const { state } = editor;
    const { from, to } = state.selection;
    let hasParagraph = false;
    let hasListItem = false;

    state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type === state.schema.nodes.listItem) hasListItem = true;

        if (node.type === state.schema.nodes.paragraph || node.type === state.schema.nodes.heading) {
            const parent = state.doc.resolve(pos).parent;
            if (parent.type !== state.schema.nodes.listItem) hasParagraph = true;
        }
    });

    return hasParagraph && hasListItem;
}

export function isCursorInNestedListItem(state: any) {
    const { $from } = state.selection;

    for (let d = $from.depth; d > 0; d--) {
        const node = $from.node(d);

        if (node.type.name === 'listItem') {
            for (let d2 = d - 1; d2 > 0; d2--) {
                const parentNode = $from.node(d2);
                if (parentNode.type.name === 'listItem') {
                    return true; // nested!
                }
            }
            return false;
        }
    }

    return false;
}


export function checkMixedOrMultipleLists(editor: Editor) {
    const { state } = editor;
    const { from, to } = state.selection;

    const listTypes = new Set();
    let listCount = 0;
    let found = false;

    state.doc.nodesBetween(from, to, (node: any, pos: number, parent: any) => {
        if (found) return false;

        if (node.type.name === 'listItem' && parent) {
            listTypes.add(parent.type.name);
            if (listTypes.size > 1) {
                found = true;
                return false;
            }
        }

        if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
            if (parent?.type.name === 'listItem') {
                return false;
            } else {
                listCount += 1;
                if (listCount > 1) {
                    found = true;
                    return false;
                }
            }
        }
        return true;
    });

    return found;
}

export function determineListAction(
    activeType: string | null,
    listType: string,
    flags: {
        isListActive: boolean;
        isSameStyleActive: boolean;
        isInsideListItem: boolean;
        isCursorOnly: boolean;
        isBlock: boolean;
        isMixedSelection: boolean;
    },
    editor: Editor,
) {

    // Toggle if same list type and active
    if (flags.isListActive && flags.isSameStyleActive) return 'toggle';

    if (isCursorInNestedListItem(editor.state)) return 'nested';

    //  Switch if active type is different
    if (activeType && activeType !== listType && !checkMixedOrMultipleLists(editor)) return 'switch';

    // Flatten for mixed selection
    if (flags.isMixedSelection || checkMixedOrMultipleLists(editor)) return 'flatten';

    // Update if inside list item
    if (flags.isInsideListItem) return 'update';

    // Switch if cursor only
    if (flags.isCursorOnly) return 'switch';

    return '';
}

export function removeColor(style: string | null) {
    if (!style) return '';
    return style
        .split(';')
        .filter((rule) => rule.trim() && !rule.trim().startsWith('color:'))
        .join('; ')
        .trim();
}

export function flattenList(
    editor: Editor,
    listNodeName: string,
    numberingType: string,
    fontSize: string,
) {
    const { state } = editor;
    let { from, to } = state.selection;
    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);

    from = $from.start($from.depth);
    to = $to.end($to.depth);

    editor
        .chain()
        .focus()
        .command(({ tr, state, dispatch }) => {
            const { doc, schema } = state;
            const listItem = schema.nodes.listItem;
            const targetList = schema.nodes[listNodeName];

            const listRoots: Array<{ start: number; end: number }> = [];

            doc.nodesBetween(from, to, (node, pos) => {
                if (node.type === schema.nodes.bulletList || node.type === schema.nodes.orderedList) {
                    listRoots.push({ start: pos, end: pos + node.nodeSize });
                    return false;
                }
                return true;
            });

            if (listRoots.length > 0) {
                from = Math.min(from, ...listRoots.map((r) => r.start));
                to = Math.max(to, ...listRoots.map((r) => r.end));
            }

            const collected: any[] = [];

            doc.nodesBetween(from, to, (node) => {
                if (node.type === schema.nodes.bulletList || node.type === schema.nodes.orderedList) {
                    node.forEach((child) => {
                        if (child.type === listItem) {
                            collected.push(listItem.create({ ...child.attrs }, child.content));
                        }
                    });
                    return false;
                }

                if (node.type.isBlock && node.type !== schema.nodes.blockquote) {
                    const marks = state.storedMarks || state?.selection?.$to.marks();
                    const textStyleMark = marks?.find((m) => m.type.name === 'textStyle');

                    const colorAttr = textStyleMark?.attrs?.color
                        ? { style: `color: ${textStyleMark.attrs.color}; ${removeColor(node.attrs.style)}` }
                        : { style: node.attrs.style };
                    const clone = node.type.create({ ...colorAttr }, node.content, node.marks);
                    collected.push(listItem.create({ ...colorAttr }, clone));
                    return false;
                }

                return true;
            });

            if (!collected.length) return false;

            tr.replaceRangeWith(
                from,
                to,
                targetList.create(
                    {
                        listStyleType: numberingType,
                        style: `font-size: ${fontSize} `,
                    },
                    collected,
                ),
            );

            if (dispatch) dispatch(tr);
            return true;
        })
        .run();
}
